import { ArrowRight, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import ScoreGauge from '../ScoreGauge';
import StatusChip from '../StatusChip';
import { scoreImpact } from '../CategoryDetailList';
import { tx } from '../../i18n';
import { confidenceCopy, formatDate } from '../../lib/format';
import { text } from '../../lib/values';
import { categoryShort, isUnrated, orderedCategories, verdictScope, verdictShort } from '../../lib/report';
import { statusIcon, statusLabel, statusOfConfidence, statusOfSeverity, statusOfVerdict } from '../../lib/status';
import type { Status } from '../../lib/status';
import type {
  AnalysisDetailResponse,
  AnalysisSummaryResponse,
  CategoryName,
  ConfidenceLevel,
  FindingView,
} from '../../types/api';

interface ReportVerdictProps {
  summary: AnalysisSummaryResponse;
  detail: AnalysisDetailResponse | null;
}

/**
 * What the number means, before anything else on the page.
 *
 * <p>A score on its own is not an answer: 80 is only good if the reader already knows the scale.
 * So the score is never shown here without the word that grades it and the sentence that explains
 * it - "80/100, a good choice", then why. Everything below this block is the evidence for this
 * block.
 */
export default function ReportVerdict({ summary, detail }: ReportVerdictProps) {
  const { score, label, confidence } = summary.overall;
  const status = statusOfVerdict(label);
  const Icon = statusIcon(status);

  return (
    <section className={`verdict verdict--${status}`} aria-labelledby="verdict-heading" data-tour="verdict">
      <span className="verdict__gauge" aria-hidden="true">
        <ScoreGauge score={score} size={104} />
      </span>

      <div className="verdict__body">
        <h2 id="verdict-heading" className="verdict__heading">
          <Icon size={22} aria-hidden="true" className="verdict__icon" />
          <span>
            <span className="verdict__score">
              {score}
              <span className="verdict__of">/100</span>
            </span>
            <span className="verdict__dash" aria-hidden="true"> — </span>
            <span className="verdict__word">{verdictShort(label)}</span>
          </span>
        </h2>

        <p className="verdict__sentence">{summary.headline}</p>

        <p className="small muted">{verdictScope()}</p>

        <p className="verdict__meta small muted">
          <ConfidenceNote level={confidence} detail={detail} />
          {/*
            * An absolute date, never "today". A report is downloaded, printed and forwarded, and
            * "Last checked today" is a lie the moment it leaves this screen.
            */}
          <span title={new Date(summary.generatedAt).toString()}>
            {tx(
              `Checked ${formatDate(summary.generatedAt)}`,
              `Kontrol: ${formatDate(summary.generatedAt)}`,
              `Sprawdzono ${formatDate(summary.generatedAt)}`,
              `Verificado em ${formatDate(summary.generatedAt)}`,
              `Comprobado el ${formatDate(summary.generatedAt)}`,
            )}
          </span>
          {detail ? null : (
            <span>
              {tx(
                'Free summary — unlock for the evidence behind each number.',
                'Ücretsiz özet — her rakamın arkasındaki kanıt için kilidi açın.',
                'Bezpłatne podsumowanie — odblokuj dowody stojące za każdą liczbą.',
                'Resumo gratuito — desbloqueie as provas por trás de cada número.',
                'Resumen gratuito — desbloquea las pruebas de cada cifra.',
              )}
            </span>
          )}
        </p>
      </div>
    </section>
  );
}

/**
 * The confidence level, and what it is a level of.
 *
 * <p>"High confidence" beside a score invites the obvious question - confidence in what, on what
 * basis - and a label that cannot answer it is decoration. One press opens the count of sources
 * that answered, the ones that did not, and when the report read them. The free tier has no
 * source list to show, so it gets the definition rather than a panel that would be empty.
 */
function ConfidenceNote({
  level,
  detail,
}: {
  level: ConfidenceLevel;
  detail: AnalysisDetailResponse | null;
}) {
  const [open, setOpen] = useState(false);
  const sources = detail?.sources ?? [];
  const answered = sources.filter((source) => source.status === 'OK').length;
  const partial = sources.filter((source) => source.status === 'PARTIAL').length;
  const missing = sources.filter((source) => source.status === 'UNAVAILABLE').length;
  const unrated = detail ? orderedCategories(detail.categories).filter(isUnrated).length : 0;

  return (
    <span className="confidence">
      <button
        type="button"
        className="confidence__button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <StatusChip status={statusOfConfidence(level)} label={confidenceCopy(level)} />
        <ChevronDown size={14} aria-hidden="true" className={open ? 'confidence__chevron is-open' : 'confidence__chevron'} />
        <span className="sr-only">
          {tx(
            'What does this confidence level mean?',
            'Bu güven seviyesi ne anlama geliyor?',
            'Co oznacza ten poziom pewności?',
            'O que significa este nível de confiança?',
            '¿Qué significa este nivel de confianza?',
          )}
        </span>
      </button>

      {open ? (
        <span className="confidence__panel">
          <span className="small">
            {tx(
              'Confidence is about the evidence, not the home: how many of the public registers answered for this exact address, and how recent their data is.',
              'Güven, konut hakkında değil kanıt hakkındadır: kamu kayıtlarından kaçının tam olarak bu adres için yanıt verdiği ve verilerinin ne kadar güncel olduğu.',
              'Pewność dotyczy dowodów, a nie mieszkania: ilu rejestrów publicznych odpowiedziało dokładnie dla tego adresu i jak świeże są ich dane.',
              'A confiança é sobre as provas, não sobre a casa: quantos registos públicos responderam para esta morada exata e quão recentes são os seus dados.',
              'La confianza va de las pruebas, no de la vivienda: cuántos registros públicos han respondido para esta dirección exacta y cómo de recientes son sus datos.',
            )}
          </span>
          {sources.length > 0 ? (
            <span className="small">
              {tx(
                `${answered} of ${sources.length} sources answered in full`,
                `${sources.length} kaynaktan ${answered} tanesi eksiksiz yanıt verdi`,
                `${answered} z ${sources.length} źródeł odpowiedziało w całości`,
                `${answered} de ${sources.length} fontes responderam na íntegra`,
                `${answered} de ${sources.length} fuentes han respondido por completo`,
              )}
              {partial > 0
                ? tx(
                    `, ${partial} in part`,
                    `, ${partial} tanesi kısmen`,
                    `, ${partial} częściowo`,
                    `, ${partial} em parte`,
                    `, ${partial} en parte`,
                  )
                : ''}
              {missing > 0
                ? tx(
                    `, ${missing} not at all`,
                    `, ${missing} tanesi hiç`,
                    `, ${missing} wcale`,
                    `, ${missing} nada`,
                    `, ${missing} nada`,
                  )
                : ''}
              {unrated > 0
                ? tx(
                    `. ${unrated} category could not be scored.`,
                    `. ${unrated} kategori puanlanamadı.`,
                    `. ${unrated} kategorii nie udało się ocenić.`,
                    `. ${unrated} categoria não pôde ser pontuada.`,
                    `. ${unrated} categoría no se ha podido puntuar.`,
                  )
                : '.'}
            </span>
          ) : null}
          <a className="small" href="#section-sources">
            {tx(
              'See every source and its date',
              'Tüm kaynakları ve tarihlerini görün',
              'Zobacz każde źródło i jego datę',
              'Ver todas as fontes e as suas datas',
              'Ver todas las fuentes y sus fechas',
            )}
          </a>
        </span>
      ) : null}
    </span>
  );
}

interface KeyFinding {
  finding: FindingView;
  category: CategoryName;
  status: Status;
  /** What the scorer says to do about this category, shown with its worst finding. */
  action: string | null;
  impact: number | null;
}

/** Every finding the report made, worst first, with the section each one belongs to. */
function collect(detail: AnalysisDetailResponse): KeyFinding[] {
  const rank: Record<Status, number> = { risk: 0, attention: 1, good: 2, unknown: 3 };
  const all: KeyFinding[] = [];
  for (const category of orderedCategories(detail.categories)) {
    for (const finding of category.findings) {
      all.push({
        finding,
        category: category.category,
        status: statusOfSeverity(finding.severity),
        action: text(category.action),
        impact: scoreImpact(finding),
      });
    }
  }
  return all.sort((a, b) => rank[a.status] - rank[b.status]);
}

/**
 * The findings that decide the answer, on the first screen.
 *
 * <p>A warning six sections down is a warning nobody read. The counts say at a glance what kind of
 * report this is, and the two or three worst findings are listed in full underneath with a link
 * into the section that evidences each one - so the top of the page is a summary of the report
 * rather than an invitation to go and find it.
 */
export function KeyFindings({ detail }: { detail: AnalysisDetailResponse }) {
  const found = collect(detail);
  if (found.length === 0) {
    return null;
  }

  const counts: Record<Status, number> = { good: 0, attention: 0, risk: 0, unknown: 0 };
  for (const item of found) {
    counts[item.status] += 1;
  }

  const worst = found.filter((item) => item.status !== 'good').slice(0, 3);
  const shown = worst.length > 0 ? worst : found.slice(0, 2);

  const countLabel = (status: Status, count: number): string => {
    switch (status) {
      case 'risk':
        return tx(
          `${count} risk${count === 1 ? '' : 's'}`,
          `${count} risk`,
          `${count} ryzyk`,
          `${count} risco${count === 1 ? '' : 's'}`,
          `${count} riesgo${count === 1 ? '' : 's'}`,
        );
      case 'attention':
        return tx(
          `${count} to check`,
          `${count} kontrol`,
          `${count} do sprawdzenia`,
          `${count} a verificar`,
          `${count} a comprobar`,
        );
      default:
        return tx(
          `${count} in this home's favour`,
          `${count} olumlu`,
          `${count} na plus`,
          `${count} a favor`,
          `${count} a favor`,
        );
    }
  };

  return (
    <section className="findings-summary" aria-labelledby="key-findings-heading">
      <div className="findings-summary__head">
        <h2 id="key-findings-heading" className="findings-summary__title">
          {tx('Key findings', 'Öne çıkan bulgular', 'Najważniejsze ustalenia', 'Principais conclusões', 'Conclusiones clave')}
        </h2>
        <p className="findings-summary__counts">
          {(['risk', 'attention', 'good'] as const)
            .filter((status) => counts[status] > 0)
            .map((status) => (
              <StatusChip key={status} status={status} label={countLabel(status, counts[status])} />
            ))}
        </p>
      </div>

      <ul className="findings-summary__list">
        {shown.map((item) => {
          const Icon = statusIcon(item.status);
          return (
            <li key={`${item.category}-${item.finding.title}`} className={`key-finding key-finding--${item.status}`}>
              <Icon size={17} aria-hidden="true" className="key-finding__icon" />
              <div className="key-finding__text">
                <p className="key-finding__title">{item.finding.title}</p>
                <p className="key-finding__marks small">
                  <span className={`finding__severity finding__severity--${item.status}`}>
                    {statusLabel(item.status)}
                  </span>
                  {item.impact !== null && item.impact !== 0 ? (
                    <span className="finding__impact">
                      {tx(
                        `${categoryShort(item.category)} ${item.impact > 0 ? '+' : '−'}${Math.abs(item.impact)} points`,
                        `${categoryShort(item.category)} ${item.impact > 0 ? '+' : '−'}${Math.abs(item.impact)} puan`,
                        `${categoryShort(item.category)} ${item.impact > 0 ? '+' : '−'}${Math.abs(item.impact)} pkt`,
                        `${categoryShort(item.category)} ${item.impact > 0 ? '+' : '−'}${Math.abs(item.impact)} pontos`,
                        `${categoryShort(item.category)} ${item.impact > 0 ? '+' : '−'}${Math.abs(item.impact)} puntos`,
                      )}
                    </span>
                  ) : null}
                </p>
                {item.finding.detail ? <p className="small muted">{item.finding.detail}</p> : null}
                {item.action && item.status !== 'good' ? (
                  <p className="small key-finding__action">
                    <strong>
                      {tx('What to do:', 'Ne yapmalı:', 'Co zrobić:', 'O que fazer:', 'Qué hacer:')}
                    </strong>{' '}
                    {item.action}
                  </p>
                ) : null}
              </div>
              <a className="key-finding__link" href={`#section-${item.category.toLowerCase()}`}>
                {tx('See the evidence', 'Kanıta bak', 'Zobacz dowody', 'Ver as provas', 'Ver las pruebas')}
                <ArrowRight size={14} aria-hidden="true" />
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
